import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type {
  UndoRedoStatus,
  SnapshotHistoryResponse,
  UndoRedoResponse,
  ClearSnapshotsResponse,
} from '../../types/undo-redo';

/**
 * pm-undo-redo-toolbar
 * 
 * Universal Undo/Redo control toolbar for tournament management.
 * Provides undo, redo, and snapshot history functionality with keyboard shortcuts.
 * 
 * @fires undo-success - Fired when undo operation succeeds
 * @fires redo-success - Fired when redo operation succeeds
 * @fires snapshots-cleared - Fired when all snapshots are cleared
 * @fires status-updated - Fired when status is refreshed
 * 
 * @example
 * ```html
 * <pm-undo-redo-toolbar
 *   tournament-id="123"
 *   show-history="true"
 *   auto-refresh="30000">
 * </pm-undo-redo-toolbar>
 * ```
 */
@customElement('pm-undo-redo-toolbar')
export class PmUndoRedoToolbar extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .toolbar-card {
      border: 1px solid var(--bs-primary, #0d6efd);
      border-radius: 0.375rem;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
      background: var(--bs-body-bg, #fff);
      padding: 1rem;
    }

    .toolbar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .toolbar-title {
      color: var(--bs-primary, #0d6efd);
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .toolbar-subtitle {
      color: var(--bs-secondary, #6c757d);
      font-size: 0.875rem;
      margin: 0;
    }

    .btn-group {
      display: flex;
      gap: 0;
    }

    .btn {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      font-weight: 500;
      border: 1px solid transparent;
      border-radius: 0;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 120px;
      justify-content: center;
    }

    .btn:first-child {
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
    }

    .btn:last-child {
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
    }

    .btn-warning {
      background-color: #ffc107;
      border-color: #ffc107;
      color: #000;
    }

    .btn-warning:hover:not(:disabled) {
      background-color: #ffca2c;
      border-color: #ffc720;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
    }

    .btn-info {
      background-color: #0dcaf0;
      border-color: #0dcaf0;
      color: #000;
    }

    .btn-info:hover:not(:disabled) {
      background-color: #31d2f2;
      border-color: #25cff2;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(13, 202, 240, 0.3);
    }

    .btn-secondary {
      background-color: #6c757d;
      border-color: #6c757d;
      color: #fff;
    }

    .btn-secondary:hover:not(:disabled) {
      background-color: #5c636a;
      border-color: #565e64;
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    .btn:active:not(:disabled) {
      animation: pulse 0.3s ease;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }

    .status-bar {
      margin-top: 0.5rem;
      font-size: 0.875rem;
      color: var(--bs-secondary, #6c757d);
      min-height: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .status-bar i {
      flex-shrink: 0;
    }

    .badge {
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      border-radius: 0.25rem;
      background-color: var(--bs-secondary, #6c757d);
      color: #fff;
    }

    .icon {
      display: inline-block;
      width: 1em;
      height: 1em;
      vertical-align: -0.125em;
    }

    .spinner {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* Modal overlay (gestito fuori dal Shadow DOM per compatibilità Bootstrap) */
    
    @media (max-width: 768px) {
      .toolbar-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
      }

      .btn-group {
        width: 100%;
        flex-direction: column;
      }

      .btn {
        width: 100%;
        border-radius: 0.375rem !important;
      }
    }
  `;

  @property({ type: Number, attribute: 'tournament-id' })
  tournamentId!: number;

  @property({ type: Boolean, attribute: 'show-history' })
  showHistory = true;

  @property({ type: Number, attribute: 'auto-refresh' })
  autoRefresh = 0; // 0 = disabled, otherwise milliseconds

  @state()
  private status: UndoRedoStatus = {
    can_undo: false,
    can_redo: false,
    undo_description: '',
    redo_description: '',
    current_sequence: 0,
    total_snapshots: 0,
  };

  @state()
  private loading = false;

  @state()
  private statusMessage = 'Caricamento stato...';

  private refreshInterval?: number;
  private keyboardHandler?: (e: KeyboardEvent) => void;

  // SVG Icons
  private svgHistory() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M75 75L41 41C25.9 25.9 0 36.6 0 57.9V168c0 13.3 10.7 24 24 24H134.1c21.4 0 32.1-25.9 17-41l-30.8-30.8C155 85.5 203 64 256 64c106 0 192 86 192 192s-86 192-192 192c-40.8 0-78.6-12.7-109.7-34.4c-14.5-10.1-34.4-6.6-44.6 7.9s-6.6 34.4 7.9 44.6C151.2 495 201.7 512 256 512c141.4 0 256-114.6 256-256S397.4 0 256 0C185.3 0 121.3 28.7 75 75zm181 53c-13.3 0-24 10.7-24 24V256c0 6.4 2.5 12.5 7 17l72 72c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-65-65V152c0-13.3-10.7-24-24-24z"/></svg>`;
  }

  private svgUndo() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M125.7 160H176c17.7 0 32 14.3 32 32s-14.3 32-32 32H48c-17.7 0-32-14.3-32-32V64c0-17.7 14.3-32 32-32s32 14.3 32 32v51.2L97.6 97.6c87.5-87.5 229.3-87.5 316.8 0s87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3s-163.8-62.5-226.3 0L125.7 160z"/></svg>`;
  }

  private svgRedo() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M386.3 160H336c-17.7 0-32 14.3-32 32s14.3 32 32 32H464c17.7 0 32-14.3 32-32V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v51.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0s-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3s163.8-62.5 226.3 0L386.3 160z"/></svg>`;
  }

  private svgList() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M40 48C26.7 48 16 58.7 16 72v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V72c0-13.3-10.7-24-24-24H40zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zM16 232v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V232c0-13.3-10.7-24-24-24H40c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V392c0-13.3-10.7-24-24-24H40z"/></svg>`;
  }

  private svgSpinner() {
    return html`<svg class="icon spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M304 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1 48 48 0 1 0 142.9 437zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437A48 48 0 1 0 437 369.1 48 48 0 1 0 369.1 437z"/></svg>`;
  }

  private svgArrowLeft() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>`;
  }

  private svgCheck() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/></svg>`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchStatus();

    // Setup auto-refresh
    if (this.autoRefresh > 0) {
      this.refreshInterval = window.setInterval(() => {
        this.fetchStatus();
      }, this.autoRefresh);
    }

    // Setup keyboard shortcuts
    this.keyboardHandler = (e: KeyboardEvent) => {
      // Ctrl+Z or Cmd+Z for Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if (this.status.can_undo && !this.loading) {
          e.preventDefault();
          this.handleUndo();
        }
      }

      // Ctrl+Y or Cmd+Shift+Z for Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        if (this.status.can_redo && !this.loading) {
          e.preventDefault();
          this.handleRedo();
        }
      }
    };
    document.addEventListener('keydown', this.keyboardHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
  }

  private getCsrfToken(): string {
    // Prova prima dal meta tag (più affidabile)
    const metaToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
    if (metaToken) {
      console.log('🔐 CSRF Token trovato nel meta tag');
      return metaToken;
    }

    // Prova dal cookie come fallback
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        console.log('🔐 CSRF Token trovato nel cookie');
        return decodeURIComponent(value);
      }
    }

    // Ultimo tentativo: cerca input hidden con csrfmiddlewaretoken
    const hiddenInput = document.querySelector<HTMLInputElement>('[name=csrfmiddlewaretoken]');
    if (hiddenInput?.value) {
      console.log('🔐 CSRF Token trovato in input hidden');
      return hiddenInput.value;
    }

    console.error('❌ CSRF Token non trovato in nessuna fonte!');
    return '';
  }

  private async fetchStatus() {
    try {
      const response = await fetch(`/simulations/tournaments/${this.tournamentId}/undo-redo-status/`);
      if (!response.ok) throw new Error('Failed to fetch status');

      this.status = await response.json();
      this.updateStatusMessage();

      this.dispatchEvent(new CustomEvent('status-updated', {
        detail: { status: this.status },
        bubbles: true,
        composed: true,
      }));
    } catch (error) {
      console.error('Errore aggiornamento stato undo/redo:', error);
      this.statusMessage = 'Errore nel caricamento dello stato';
    }
  }

  private updateStatusMessage() {
    let message = '';

    if (this.status.can_undo) {
      message += `← ${this.status.undo_description}`;
    }
    if (this.status.can_redo) {
      if (message) message += ' | ';
      message += `→ ${this.status.redo_description}`;
    }
    if (!message) {
      message = '✓ Nessuna azione disponibile';
    }

    this.statusMessage = message;
  }

  private async handleUndo() {
    this.loading = true;
    this.statusMessage = 'Annullamento in corso...';

    try {
      const csrfToken = this.getCsrfToken();
      console.log('🔐 CSRF Token:', csrfToken ? 'Present' : 'Missing');
      
      const response = await fetch(`/simulations/tournaments/${this.tournamentId}/undo/`, {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Undo response status:', response.status, response.statusText);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Errore HTTP ${response.status}: ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            console.error('Failed to parse error JSON:', e);
          }
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', text.substring(0, 200));
        }
        
        this.showToast('danger', errorMessage);
        this.loading = false;
        await this.fetchStatus();
        return;
      }

      const data: UndoRedoResponse = await response.json();

      if (data.success) {
        this.showToast('success', data.message);
        this.dispatchEvent(new CustomEvent('undo-success', {
          detail: { message: data.message },
          bubbles: true,
          composed: true,
        }));

        // Ricarica pagina dopo 1 secondo
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        this.showToast('danger', data.message);
        this.loading = false;
        await this.fetchStatus();
      }
    } catch (error) {
      console.error('Errore durante undo:', error);
      this.showToast('danger', "Errore di connessione durante l'annullamento");
      this.loading = false;
      await this.fetchStatus();
    }
  }

  private async handleRedo() {
    this.loading = true;
    this.statusMessage = 'Ripristino in corso...';

    try {
      const csrfToken = this.getCsrfToken();
      console.log('🔐 CSRF Token for redo:', csrfToken ? 'Present' : 'Missing');
      
      const response = await fetch(`/simulations/tournaments/${this.tournamentId}/redo/`, {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Redo response status:', response.status, response.statusText);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Errore HTTP ${response.status}: ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            console.error('Failed to parse error JSON:', e);
          }
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', text.substring(0, 200));
        }
        
        this.showToast('danger', errorMessage);
        this.loading = false;
        await this.fetchStatus();
        return;
      }

      const data: UndoRedoResponse = await response.json();

      if (data.success) {
        this.showToast('success', data.message);
        this.dispatchEvent(new CustomEvent('redo-success', {
          detail: { message: data.message },
          bubbles: true,
          composed: true,
        }));

        // Ricarica pagina dopo 1 secondo
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        this.showToast('danger', data.message);
        this.loading = false;
        await this.fetchStatus();
      }
    } catch (error) {
      console.error('Errore durante redo:', error);
      this.showToast('danger', 'Errore di connessione durante il ripristino');
      this.loading = false;
      await this.fetchStatus();
    }
  }

  private async handleShowHistory() {
    try {
      const response = await fetch(`/simulations/tournaments/${this.tournamentId}/snapshot-history/?limit=50`);
      if (!response.ok) throw new Error('Failed to fetch history');

      const data: SnapshotHistoryResponse = await response.json();
      this.renderHistoryModal(data);
    } catch (error) {
      console.error('Errore caricamento storico:', error);
      this.showToast('danger', 'Errore durante il caricamento dello storico');
    }
  }

  private renderHistoryModal(data: SnapshotHistoryResponse) {
    // Rimuovi modale precedente se esiste
    const existingModal = document.getElementById('snapshotHistoryModal');
    if (existingModal) {
      existingModal.remove();
    }

    const typeIcons: Record<string, string> = {
      score_insert: 'fa-plus-circle',
      score_update: 'fa-edit',
      bracket_advance: 'fa-forward',
      round_delete: 'fa-trash',
      manual_edit: 'fa-hand-pointer',
    };

    const typeColors: Record<string, string> = {
      score_insert: 'success',
      score_update: 'info',
      bracket_advance: 'primary',
      round_delete: 'warning',
      manual_edit: 'secondary',
    };

    const historyItems = data.history
      .map((snapshot) => {
        const isCurrent = snapshot.is_current;
        const icon = typeIcons[snapshot.action_type] || 'fa-circle';
        const color = typeColors[snapshot.action_type] || 'secondary';

        return `
          <div class="list-group-item ${isCurrent ? 'active' : ''}">
            <div class="d-flex w-100 justify-content-between align-items-start">
              <div>
                <h6 class="mb-1">
                  <i class="fas ${icon} text-${isCurrent ? 'white' : color} me-2"></i>
                  ${snapshot.description}
                </h6>
                <p class="mb-1 small">
                  <i class="fas fa-user me-1"></i>${snapshot.user}
                  <i class="fas fa-clock ms-2 me-1"></i>${new Date(snapshot.timestamp).toLocaleString('it-IT')}
                </p>
              </div>
              <div>
                <span class="badge ${isCurrent ? 'bg-light text-dark' : 'bg-secondary'}">#${snapshot.sequence}</span>
                ${isCurrent ? '<span class="badge bg-success ms-1">CORRENTE</span>' : ''}
              </div>
            </div>
          </div>
        `;
      })
      .join('');

    const modalHtml = `
      <div class="modal fade" id="snapshotHistoryModal" tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fas fa-history me-2"></i>Storico Azioni - ${data.tournament_name}
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="list-group">
                ${historyItems}
              </div>
            </div>
            <div class="modal-footer d-flex justify-content-between">
              <button type="button" class="btn btn-danger" id="clearAllSnapshotsBtn">
                <i class="fas fa-trash-alt me-1"></i>Azzera Tutto
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Chiudi</button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Setup modal
    const modalElement = document.getElementById('snapshotHistoryModal')!;
    // @ts-ignore - Bootstrap modal
    const modal = new bootstrap.Modal(modalElement);
    modal.show();

    // Setup clear button
    const clearBtn = document.getElementById('clearAllSnapshotsBtn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.handleClearSnapshots(modal);
      });
    }

    // Remove modal when closed
    modalElement.addEventListener('hidden.bs.modal', () => {
      modalElement.remove();
    });
  }

  private async handleClearSnapshots(modal: any) {
    const confirmFirst = confirm(
      '⚠️ ATTENZIONE!\n\n' +
        'Stai per eliminare lo storico delle azioni di questo torneo.\n\n' +
        '⚠️ NOTA IMPORTANTE:\n' +
        '• Lo snapshot INIZIALE (bracket generato) sarà PRESERVATO\n' +
        '• Tutti gli altri snapshot saranno eliminati\n' +
        '• Questa azione è IRREVERSIBILE\n' +
        '• Non sarà possibile recuperare lo storico eliminato\n\n' +
        'Continuare?'
    );

    if (!confirmFirst) return;

    try {
      const response = await fetch(`/simulations/tournaments/${this.tournamentId}/clear-snapshots/`, {
        method: 'POST',
        headers: {
          'X-CSRFToken': this.getCsrfToken(),
          'Content-Type': 'application/json',
        },
      });

      const data: ClearSnapshotsResponse = await response.json();

      if (data.success) {
        this.showToast('success', data.message);

        // Chiudi modale
        modal.hide();

        // Aggiorna stato
        await this.fetchStatus();

        this.dispatchEvent(new CustomEvent('snapshots-cleared', {
          detail: { count: data.deleted_count },
          bubbles: true,
          composed: true,
        }));
      } else {
        this.showToast('danger', data.message);
      }
    } catch (error) {
      console.error('Errore eliminazione snapshot:', error);
      this.showToast('danger', "Errore durante l'eliminazione degli snapshot");
    }
  }

  private showToast(variant: 'success' | 'danger', message: string) {
    // Usa il sistema pm-toast globale
    const globalShowToast = (window as any)?.PadelMatcher?.ui?.showToast || (window as any).showToast;

    if (globalShowToast) {
      globalShowToast({
        message,
        variant,
        autohide: 5000,
      });
    } else {
      // Fallback: usa direttamente il componente pm-toast
      const toastElement = document.querySelector('pm-toast#pm-global-toast') as any;

      if (toastElement && typeof toastElement.show === 'function') {
        toastElement.show({
          message,
          variant,
          autohide: 5000,
        });
      } else {
        // Ultimo fallback: console
        console.log(`[${variant}] ${message}`);
      }
    }
  }

  render() {
    return html`
      <div class="toolbar-card">
        <div class="toolbar-header">
          <div>
            <h6 class="toolbar-title">
              ${this.svgHistory()}
              Controllo Azioni
            </h6>
            <p class="toolbar-subtitle">Annulla o ripristina le ultime modifiche al torneo</p>
          </div>
          <div class="btn-group" role="group">
            <button
              type="button"
              class="btn btn-warning"
              @click=${this.handleUndo}
              ?disabled=${!this.status.can_undo || this.loading}
              title=${this.status.can_undo ? this.status.undo_description : 'Nessuna azione da annullare'}
            >
              ${this.svgUndo()}
              Annulla
            </button>
            <button
              type="button"
              class="btn btn-info"
              @click=${this.handleRedo}
              ?disabled=${!this.status.can_redo || this.loading}
              title=${this.status.can_redo ? this.status.redo_description : 'Nessuna azione da ripristinare'}
            >
              ${this.svgRedo()}
              Ripristina
            </button>
            ${this.showHistory
              ? html`
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click=${this.handleShowHistory}
                    ?disabled=${this.loading}
                    title="Visualizza storico completo"
                  >
                    ${this.svgList()}
                    Storico
                  </button>
                `
              : ''}
          </div>
        </div>
        <div class="status-bar">
          ${this.loading
            ? this.svgSpinner()
            : this.status.can_undo
            ? html`<span style="color: #ffc107;">${this.svgArrowLeft()}</span>`
            : html`<span style="color: #198754;">${this.svgCheck()}</span>`}
          <span>${this.statusMessage}</span>
          <span class="badge">${this.status.current_sequence}/${this.status.total_snapshots}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-undo-redo-toolbar': PmUndoRedoToolbar;
  }
}
